'use client';

import { Ref, ComponentProps, ChangeEvent, useState, useEffect } from 'react';
import { TextInput } from '../text-input/text-input';

type Value = number | undefined | '-0';

const centsToString = (value: Value) => {
  if (value === undefined) return '';
  if (value === '-0') return '-$0.00';
  const isNegative = value < 0;
  const str = String(Math.abs(value)).replace(/(\d+)(\d\d)$/, '$1.$2');
  const pad = '0.00'.substring(0, 4 - str.length);
  return (isNegative ? '-' : '') + '$' + pad + str;
};

const stringToCents = (input: string): Value => {
  const trimmed = input.trim();
  if (trimmed === '') return undefined;
  const isNegative = trimmed.match(/-/g)?.length === 1;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.match(/^0*$/) && isNegative) return '-0';
  return isNegative ? -parseInt(digits) : parseInt(digits);
};

export type CurrencyInputProps = Omit<ComponentProps<typeof TextInput>, 'value' | 'onChange'> & {
  value?: number;
  onChange?: (v: number) => void;
  allowNegatives?: boolean;
  ref?: Ref<HTMLInputElement>;
};

export const CurrencyInput = (props: CurrencyInputProps) => {
  const { allowNegatives, value, onChange, ref, ...rest } = props;
  const [input, setInput] = useState(centsToString(value));

  useEffect(() => {
    setInput(centsToString(value));
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const num = stringToCents(allowNegatives ? v : v.replace(/-/g, ''));
    setInput(centsToString(num));
    onChange?.(typeof num === 'number' ? num : 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (e.currentTarget.value.trim().replace(/[\D0]/g, '') === '') {
        e.preventDefault();
        setInput('');
        onChange?.(0);
      }
    }
  };

  return (
    <TextInput
      ref={ref}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      value={input}
      placeholder="$0.00"
      css={{ textAlign: 'right' }}
      {...rest}
    />
  );
};
